import { Box, HStack, Tooltip } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Text,
} from 'components/v4';
import MfaChakraCodeBox from './MfaChakraCodeBox';

import { useTpmContext, useTpmState, useTpmActions } from './';

import {
    postRequestNumberVerification,
    postSubmitNumberVerification,
} from 'API/TPMTrial';
import walkthroughIds from './walkthroughIds';

import {
    RESEND,
    RESET,
    SUBMIT_NUMBER,
    SET_TPM_COUNTRIES,
} from '@/store/constants';
import { useAssociatedAccounts } from 'hooks/useCustomQuery';
import { CODE_EXPIRY } from './tpmReducer';

export default function MfaTeamsPhoneMobileStepOne({ onClose: onDrawerClose }) {
    const { tpmNumber, tpmActivatedNumber } = useTpmContext();
    const state = useTpmState();
    const actions = useTpmActions();
    const [tpmState, setTpmState] = tpmNumber;
    const [, setTelephoneNumbers] = tpmActivatedNumber;

    // Local MFA state
    const [mfaCode, setMfaCode] = useState('');
    const [mfaCodeError, setMfaCodeError] = useState({
        error: false,
        message: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [hasEnteredCode, setHasEnteredCode] = useState(false);

    const { id } = useSelector((s) => s?.modal?.state?.service || {});
    const [searchParams] = useSearchParams();
    const { darkMode } = useSelector((s) => s.settings);
    const {
        number: mpn,
        lastUpdate,
        countries,
    } = useSelector((s) => s.service.tpm);
    const dispatch = useDispatch();

    const phoneNumber = state.phoneNumber || tpmState.number || mpn;
    const currentTime = Date.now();
    const [timeElapse, setTimeElapse] = useState(
        currentTime - (state.lastCodeSentTime || lastUpdate || currentTime),
    );

    const { refetch } = useAssociatedAccounts(id);

    const handleMfaCodeChange = (value) => {
        setMfaCode(value);
        setHasEnteredCode(true);
        actions.setVerificationCode(value);

        // Auto-submit on complete
        if (value.length === 6) {
            submitMfaCode(value);
        }
    };

    const handleResend = () => {
        actions.resendCode();
        setMfaCode('');
        setMfaCodeError({ error: false, message: '' });

        postRequestNumberVerification({
            serviceID: id,
            phonenumber: phoneNumber,
        })
            .then(() => {
                setMfaCodeError({ error: false, message: '' });
            })
            .catch((err) => {
                const errMsg =
                    err?.response?.data?.message || 'Something went wrong';
                setMfaCodeError({ error: true, message: errMsg });
                actions.submitCodeFailure(errMsg);
            });

        dispatch({ type: RESEND });

        // Focus first input
        const pinInputs = document.querySelectorAll(
            'input[class*="chakra-pin-input"]',
        );
        if (pinInputs?.length > 0) {
            pinInputs[0].focus();
        }
    };

    const { ufMfaNextButton, ufMfaCancelButton, ufMfaResetButton } =
        walkthroughIds.teamsPhoneMobileTrial;

    const number = new URLSearchParams(searchParams).get('number');

    const submitMfaCode = useCallback(
        async (code) => {
            if (code?.length !== 6) return;

            setIsLoading(true);
            actions.submitCodeStart();

            postSubmitNumberVerification({
                serviceID: id,
                phonenumber: phoneNumber || number,
                code: code,
            })
                .then(async (res) => {
                    if (res.status === 200) {
                        // Clear code to prevent re-submission
                        setMfaCode('');
                        setHasEnteredCode(false);

                        setTpmState((prev) => ({
                            ...prev,
                            number: phoneNumber,
                            status: 'PASS',
                        }));

                        // Automatically add the newly verified number to the
                        // Associate Numbers list so it can be associated
                        // without the user re-entering it.
                        setTelephoneNumbers((prev) => {
                            if (
                                prev.some((item) => item.number === phoneNumber)
                            ) {
                                return prev;
                            }
                            const withoutEmptyRows = prev.filter(
                                (item) => item.number !== '',
                            );
                            return [
                                ...withoutEmptyRows,
                                { number: phoneNumber, status: 'IDLE' },
                            ];
                        });

                        dispatch({
                            type: SUBMIT_NUMBER,
                            payload: { code: code },
                        });

                        await refetch();
                        toast.success(
                            'Successfully verified your phone number.',
                        );

                        // Reset state
                        actions.submitCodeSuccess();
                        dispatch({ type: RESET });
                        dispatch({
                            type: SET_TPM_COUNTRIES,
                            payload: countries,
                        });
                    }
                })
                .catch((e) => {
                    const errorMsg =
                        e.response?.data?.message ||
                        'Something went wrong. Please try again later.';
                    setMfaCodeError({ error: true, message: errorMsg });
                    actions.submitCodeFailure(errorMsg);
                    console.error(e);
                })
                .finally(() => setIsLoading(false));
        },
        [
            id,
            phoneNumber,
            number,
            setTpmState,
            setTelephoneNumbers,
            countries,
            dispatch,
            refetch,
            actions,
        ],
    );

    const handleBackButtonClick = () => {
        actions.goToScreen('NUMBER_INPUT');
        setTpmState((prev) => ({
            ...prev,
            status: 'IDLE',
        }));
        onDrawerClose();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (hasEnteredCode) {
            submitMfaCode(mfaCode);
        }
    };

    const handleReset = () => {
        actions.reset();
        dispatch({ type: RESET });
    };

    // Update time elapsed for code expiry
    useEffect(() => {
        const interval = setInterval(() => {
            setTimeElapse(
                Date.now() - (state.lastCodeSentTime || lastUpdate || 0),
            );
        }, 1000);
        return () => clearInterval(interval);
    }, [state.lastCodeSentTime, lastUpdate]);

    const isCodeExpired = timeElapse > CODE_EXPIRY;
    const maskedNumber = phoneNumber?.replace(/\d(?=\d{4})/g, 'X');

    return (
        <Box as="form" onSubmit={handleSubmit}>
            <Card bg={darkMode ? 'dark.bgDarkGray' : 'white'}>
                <CardHeader>
                    <Text className="text-xl font-semibold">
                        Associate New Account
                    </Text>
                </CardHeader>
                <Box px={6} display="flex" flexDir="column" mx="auto">
                    <CardBody p={0}>
                        <Text fontSize="md" sx={{ marginBottom: 3 }}>
                            You have been sent a Teams Phone Mobile verification
                            code by your mobile Operator to the number{' '}
                            <strong>{maskedNumber}</strong>
                        </Text>
                        {isCodeExpired ? (
                            <Text textAlign="center">
                                <Button
                                    variant="link"
                                    onClick={handleResend}
                                    fontWeight="400">
                                    Resend code
                                </Button>
                            </Text>
                        ) : (
                            <Text fontSize="md" textAlign="center">
                                Please enter the code below
                            </Text>
                        )}
                        <Box color={darkMode ? 'white' : 'gray.700'}>
                            <MfaChakraCodeBox
                                otp={mfaCode}
                                onComplete={handleMfaCodeChange}
                                onChange={(value) => {
                                    setMfaCode(value);
                                    actions.setVerificationCode(value);
                                }}
                            />
                        </Box>
                        {mfaCodeError.error && (
                            <>
                                <Alert status="error">
                                    <AlertIcon />
                                    <AlertDescription>
                                        <Text>
                                            {mfaCodeError.message ||
                                                'The code you entered is incorrect.'}
                                        </Text>
                                    </AlertDescription>
                                </Alert>
                                <Box className="mb-6" />
                            </>
                        )}
                    </CardBody>

                    <CardFooter pt={0} px={0} className="px-[1.25rem]">
                        <HStack width="100%" alignItems="center" spacing={6}>
                            <Button
                                variant="outline"
                                minW="128px"
                                bg={darkMode ? 'white' : null}
                                onClick={handleBackButtonClick}
                                data-walkthroughid={ufMfaCancelButton}>
                                Back
                            </Button>
                            <Button
                                type="submit"
                                minW="128px"
                                isDisabled={!hasEnteredCode || isLoading}
                                isLoading={isLoading}
                                loadingText="Submitting"
                                data-walkthroughid={ufMfaNextButton}>
                                Next
                            </Button>

                            <Tooltip
                                hasArrow
                                shouldWrapChildren
                                label="Go back to the first step. Your phone number is still processing at the server until the request expires though.">
                                <Button
                                    minW="128px"
                                    colorScheme="red"
                                    onClick={handleReset}
                                    data-walkthroughid={ufMfaResetButton}>
                                    Reset
                                </Button>
                            </Tooltip>
                        </HStack>
                    </CardFooter>
                </Box>
            </Card>
        </Box>
    );
}
